import axios from 'axios';
import type { SiteId } from '@hotjar/site';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import type { Clause } from '@hotjar/feedback/domain/Surveys/Responses/Filters/Clause';
import type { VotingAction } from '@hotjar/feedback/domain/common/Responses/VotingAction';
import type { SurveyResponseDto } from './Responses/SurveyResponseDto';
import type { Survey } from './Survey/Survey';
import type { SurveyQuestion } from './Questions';

function getEndpointPath(
  version: 'v1' | 'v2' | 'v3' | 'v4',
  siteId: SiteId,
  entityPath: 'polls' | 'survey' | 'surveys' = 'polls',
  surveyId?: number,
  action?: 'move',
): string {
  let path = `/api/${version}/sites/${siteId}/${entityPath}`;

  if (surveyId) {
    path = `${path}/${surveyId}`;
  }

  if (action) {
    path = `${path}/${action}`;
  }
  return path;
}

export async function saveSurveyWidget(siteId: SiteId, survey: Survey): Promise<void> {
  const path = getEndpointPath('v1', siteId, 'polls', survey.id);

  const response = await axios.put<ApiBaseResponse>(path, survey);

  if (!response.data.success) {
    throw new Error();
  }
}

export async function toggleSurveyStatus(siteId: SiteId, surveyId: number): Promise<void> {
  const path = getEndpointPath('v1', siteId, 'polls', surveyId);

  const response = await axios.post<ApiBaseResponse>(path, { action: 'toggle_enabled' });

  if (!response.data.success) {
    throw new Error();
  }
}

export async function getSurveyWidgets(siteId: SiteId): Promise<Survey[]> {
  const response = await axios.get(getEndpointPath('v1', siteId, 'surveys'));

  return response.data;
}

export async function getSurveyWidget(siteId: SiteId, surveyId: number): Promise<Survey> {
  const response = await axios.get(getEndpointPath('v1', siteId, 'polls', surveyId));

  return response.data;
}

export async function deleteSurveyWidget(siteId?: SiteId, widgetId?: number): Promise<void> {
  if (siteId === undefined) {
    throw new Error('Invalid site id');
  }

  if (widgetId === undefined) {
    throw new Error('Invalid widget id');
  }

  const response = await axios.delete(getEndpointPath('v4', siteId, 'surveys', widgetId));

  if (!response.data.success) throw new Error();
}

export async function deleteSurveyResponse(
  siteId?: SiteId,
  widgetId?: number,
  responseId?: number,
): Promise<void> {
  if (siteId === undefined) {
    throw new Error('Invalid site id');
  }

  if (widgetId === undefined) {
    throw new Error('Invalid widget id');
  }

  if (responseId === undefined) {
    throw new Error('Invalid widget id');
  }

  const response = await axios.delete(
    `${getEndpointPath('v1', siteId, 'polls', widgetId)}/responses/${responseId}`,
  );

  if (!response.data.success) {
    throw new Error();
  }
}

export async function getSurveyResponse(
  siteId: SiteId,
  surveyId: number,
  responseId: number,
): Promise<SurveyResponseDto> {
  const response = await axios.get(
    `/api/v3/sites/${siteId}/polls/${surveyId}/responses/${responseId}`,
  );

  return response.data;
}

interface SurveyResponsesResult {
  responses: SurveyResponseDto[];
  success: boolean;
}

export async function getSurveyResponses(
  siteId: SiteId,
  surveyId: number,
  sort: string,
  clauses: Clause[],
  pageNumber: number,
  pageSize: number,
): Promise<SurveyResponsesResult> {
  const response = await axios.get(`/api/v3/sites/${siteId}/polls/${surveyId}/responses`, {
    params: {
      survey_query: {
        sort_by: sort,
        page_number: pageNumber,
        page_size: pageSize,
        clauses,
      },
    },
  });

  return response.data;
}

interface SurveyResponseCountDto {
  count: number;
  success: boolean;
}

export async function getSurveyResponseCount(
  siteId: SiteId,
  surveyId: number,
  clauses: Clause[],
): Promise<SurveyResponseCountDto> {
  const response = await axios.get(`/api/v3/sites/${siteId}/polls/${surveyId}/count`, {
    params: {
      survey_query: {
        clauses,
      },
    },
  });

  return response.data;
}

export function sendResponseFeedback(
  votingAction: VotingAction,
  siteId: SiteId,
  surveyId: number,
  responseId: number,
): Promise<void> {
  return axios.put(`/api/v1/sites/${siteId}/polls/${surveyId}/responses/${responseId}`, {
    voting_action: votingAction,
  });
}

interface SurveyQuestionsResult {
  questions: SurveyQuestion[];
}

export async function getSurveyQuestions(
  siteId: SiteId,
  widgetId: number,
): Promise<SurveyQuestionsResult> {
  const response = await axios.get(`${getEndpointPath('v1', siteId, 'polls', widgetId)}/questions`);

  return response.data;
}

export async function getSurveyOverview({
  siteId,
  surveyId,
}: {
  siteId: SiteId;
  surveyId: number;
}): Promise<SurveyQuestionsResult> {
  const response = await axios.get(
    `${getEndpointPath('v1', siteId, 'surveys', surveyId)}/overview`,
  );

  return response.data;
}

export async function getSurveyBreakdown({
  siteId,
  surveyId,
  filters,
}: {
  siteId: SiteId;
  surveyId: number;
  filters: Clause[];
}): Promise<SurveyQuestionsResult> {
  const response = await axios.get(
    `${getEndpointPath('v1', siteId, 'surveys', surveyId)}/breakdown`,
    { params: { survey_query: { filters } } },
  );

  return response.data;
}
